import { RejectOption, RejectRegimeScope, RejectSubOption } from "../interfaces/reject-option.interface";
import { VALID_EMAIL_REGEX } from "./email-validator";

export class GeneralUtilities {
  public static terms = [24, 36, 48, 60];

  public static nexu_phone_numbers = ["55 4161 4850"];
  public static nexu_email_regex = /nexu.mx/;

  public static nexu_info = {
    address: "Rio Elba #20, Piso 15, Col. Cuauhtemoc, 06500, CDMX, México.",
    gps_email: "gps@nexu.mx",
    rfc: "PRE140722U30",
    business_name: "PRESTEMOS SAPI DE CV",
    postal_code: "06500",
    birth_date: "22/07/2014",
  };

  public static regimes = [
    { value: "pf", viewValue: "Persona Física" },
    { value: "pfae", viewValue: "Persona Física con Actividad Empresarial" },
    { value: "pm", viewValue: "Persona Moral" },
  ];

  public static goals = [
    { value: "added", viewValue: "Ingresos" },
    { value: "preauth", viewValue: "Preautorizaciones" },
    { value: "opportunities", viewValue: "Oportunidades" },
    { value: "sent", viewValue: "Solicitudes" },
    { value: "approved", viewValue: "Aprobaciones" },
    { value: "payed", viewValue: "Cierres" },
  ];

  public static skills = [
    { value: "advanced", viewValue: "Especialista sobresaliente", viewShortValue: "Sobresaliente (ES)" },
    { value: "experimented", viewValue: "Especialista experimentado", viewShortValue: "Experimentado (EE)" },
    { value: "trainee", viewValue: "Especialista en aprendizaje", viewShortValue: "En aprendizaje (EA)" },
  ];

  public static disbursement_status = [
    { value: "requested", viewValue: "Solicitado" },
    { value: "incomplete", viewValue: "Incompleta" },
    { value: "in_process", viewValue: "En proceso" },
    { value: "payed", viewValue: "Completado" },
  ];

  public static downpayment_status = [
    { value: "requested", viewValue: "Solicitado" },
    { value: "accepted", viewValue: "Aceptado" },
    { value: "rejected", viewValue: "Rechazado" },
  ];

  public static downpayment_concept = [
    { value: "initial_payment", viewValue: "Pago inicial" },
    { value: "tenure", viewValue: "Tenencia" },
    { value: "extra_accessories", viewValue: "Accesorios extras" },
  ];

  public static downpayment_payed_to = [{ value: "agency", viewValue: "Agencia" }];

  public static downpayment_scope_names = [
    { value: "pendings", viewValue: "Pendientes" },
    { value: "reviewed", viewValue: "Revisados" },
  ];

  public static extensionToType = {
    pdf: "application/pdf",
    csv: "text/csv;charset=utf-8",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };

  public static stage_status = [
    { value: "approved", name: "Esperando pago inicial" },
    { value: "purchase_order", name: "Orden de compra" },
    { value: "contract", name: "Contrato" },
    { value: "disbursement", name: "Desembolso" },
    { value: "delivering", name: "En proceso de entrega" },
    { value: "delivered", name: "Entregado" },
  ];
  public static mail_categories: any = [
    { value: "legacy", label: "Sin definir" },
    { value: "personal", label: "Personal" },
    { value: "work", label: "Trabajo" },
  ];
  public static proposal_statuses = [
    { value: "", view: "Sin enviar" },
    { value: false, view: "Propuesta enviada" },
    { value: true, view: "Propuesta aceptada" },
  ];
  public static months: string[] = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];
  public static document_category_names = {
    income_proof: "Ingresos",
    identification: "Identificación",
    address_proof: "Domicilio",
    complementary: "Complementarios",
    unnecessary: "Innecesarios",
    application: "Solicitud",
    buro: "Buró de Crédito",
    marriage_certificate: "Acta de matrimonio",
    endorsement_identification: "Identificación (Aval)",
    endorsement_bank_statement: "Ingresos (Aval)",
    endorsement_birth_certificate: "Acta de nacimiento (Aval)",
    birth_certificate: "Acta de nacimiento (titular)",
    references_call: "Llamada a referencias",
    endorsement_address_proof: "Domicilio (Aval)",
  };

  public static rejected_reasons = {
    r1: "R1 - Capacidad y consistencia de ingresos",
    r2: "R2 - Perfil de alto riesgo",
    r3: "R3 - Inconsistencias de información",
    r4: "R4 - Documentos alterados",
    r5: "R5 - Rechazo por políticas",
    r6: "R6 - Aval no cumple",
    r7: "R7 - Titular no desea el financiamiento",
  };

  /**
   * Display label for reject reasons: "R1 Capacidad de pago" instead of "R1 - Capacidad de pago".
   */
  public static formatRejectedReasonLabel(label: string | undefined): string {
    if (!label) {
      return "";
    }
    return label.replace(/\s-\s/, " ");
  }

  public static reject_options: RejectOption[] = [
    {
      label: "R1 - Capacidad y consistencia de ingresos",
      pm_label: "R1 - No cumple Aforo requerido",
      value: "r1",
      sub_options: [
        { label: "Plataforma no brinda opción con ingreso calculado", value: "r1-1", scope: "pf" },
        { label: "Sin depósitos relacionados con actividad económica", value: "r1-2", scope: "pf" },
        { label: "No hay respaldo de actividad", value: "r1-3", scope: "pf" },
        { label: "Triangulación de ingresos (posible perfilamiento)", value: "r1-4", scope: "pf" },
        { label: "Rechazo por sobre endeudamiento", value: "r1-5", scope: "pf" },
        { label: "Inconsistencias entre nivel económico del cliente y crédito solicitado", value: "r1-6", scope: "pf" },
        { label: "Sin capacidad de pago", value: "r1-7", scope: "pf" },
        { label: "Empresa sin liquidez", value: "r1-8", scope: "pm" },
        { label: "Empresa con quiebra técnica", value: "r1-9", scope: "pm" },
        { label: "Empresa con alto apalancamiento", value: "r1-10", scope: "pm" },
        { label: "Empresa sin utilidad", value: "r1-11", scope: "pm" },
        { label: "Empresa con pérdidas fiscales", value: "r1-12", scope: "pm" },
        { label: "Estados de cuenta con saldos promedio y finales bajos", value: "r1-13", scope: "pm" },
        { label: "Estados de cuenta sin ingresos de la actividad económica", value: "r1-14", scope: "pm" },
      ],
    },
    {
      label: "R2 - Perfil de alto riesgo",
      value: "r2",
      sub_options: [
        // { label: "Comercio ilegítimo", value: "r2-1" }, Deprecated, same as r5-5
        // { label: "Perfil de riesgo", value: "r2-2" }, Deprecated
        // { label: "Persona políticamente expuesta", value: "r2-3" }, changed to r5
        { label: "Noticias negativas en la red", value: "r2-4", scope: "pf" },
        { label: "Demanda por fraude o robo", value: "r2-5", scope: "all" },
        // { label: "Suplantación de identidad", value: "r2-6" }, changed to r3
        { label: "Se niega a compartir información", value: "r2-8", scope: "pf" },
        {
          label:
            "Riesgo crediticio (Exceso de consultas, multiples domicilios, atrasos créditos automotrices, préstamos a corto plazo)",
          value: "r2-9",
          scope: "pf",
        },
        {
          label:
            "Comportamiento en estados de cuenta (Sin gastos vida, saldos promedio bajos, sobregiros en cuenta, domiciliaciones rechazadas)",
          value: "r2-10",
          scope: "pf",
        },
        { label: "Alertas en medios", value: "r2-11", scope: "pm" },
        { label: "Antecedentes penales", value: "r2-12", scope: "pm" },
        {
          label: "Riesgo crediticio (créditos con mal historial y exceso de consultas)",
          value: "r2-13",
          scope: "pm",
        },
        { label: "Dependencia de un solo cliente", value: "r2-14", scope: "pm" },
      ],
    },
    {
      label: "R3 - Inconsistencias de información",
      value: "r3",
      sub_options: [
        { label: "Reconocimiento de domicilio falso", value: "r3-1", scope: "pf" },
        {
          label: "Datos personales no coinciden vs información declarada en investigación telefónica",
          value: "r3-2",
          scope: "pf",
        },
        {
          label:
            "Diferencia en actividad declarada en documentación vs información declarada en investigación telefónica",
          value: "r3-3",
          scope: "pf",
        },
        { label: "Ayuda de terceros al contestar investigación telefónica", value: "r3-4", scope: "pf" },
        // { label: "Triangulación de ingresos buscando engañar", value: "r3-5" }, considered on r1-4
        { label: "Reconocimiento de empleo o actividad económica falsa", value: "r3-6", scope: "pf" },
        { label: "Inconsistencia en información declarada por referencias", value: "r3-7", scope: "pf" },
        { label: "Inconsistencias en videollamada", value: "r3-8", scope: "pf" },
        { label: "Suplantación de identidad", value: "r3-9", scope: "all" },
        {
          label: "Diferencia en información declarada en investigación telefónica vs documentación",
          value: "r3-10",
          scope: "pm",
        },
        {
          label: "Diferencia en información declarada en investigación telefónica vs Syntage",
          value: "r3-11",
          scope: "pm",
        },
      ],
    },
    {
      label: "R4 - Documentos alterados",
      value: "r4",
      sub_options: [
        { label: "Identificación oficial", value: "r4-1", scope: "all" },
        { label: "Comprobante de domicilio ", value: "r4-2", scope: "all" },
        { label: "Estados de cuenta", value: "r4-3", scope: "all" },
        { label: "Complementarios", value: "r4-4", scope: "all" },
        { label: "Acta constitutiva", value: "r4-5", scope: "pm" },
        { label: "Constancia situación fiscal representante legal", value: "r4-6", scope: "pm" },
      ],
    },
    {
      label: "R5 - Rechazo por políticas",
      value: "r5",
      sub_options: [
        { label: "Unidad no financiable", value: "r5-1", scope: "all" },
        { label: "Uso de unidad en plataformas de transporte privado (Ej. uber)", value: "r5-2", scope: "pf" },
        { label: "Edad avanzada", value: "r5-3", scope: "pf" },
        {
          label: "Pensionados y jubilados recientes por no cumplimiento de comprobación de ingresos",
          value: "r5-4",
          scope: "pf",
        },
        { label: "Actividad fuera de política", value: "r5-5", scope: "all" },
        { label: "No cumple política para segunda o tercer unidad", value: "r5-6", scope: "all" },
        { label: "Persona políticamente expuesta", value: "r5-7", scope: "pf" },
        { label: "Representante legal políticamente expuesto", value: "r5-8", scope: "pm" },
        {
          label: "Modificación a la unidad (Rótulos, sirenas, instalación de gps, otros)",
          value: "r5-9",
          scope: "pm",
        },
        { label: "Monto no financiable", value: "r5-10", scope: "pm" },
        { label: "Uso de unidad en plataformas de transporte privado (Ej. Urbvan)", value: "r5-11", scope: "pm" },
      ],
    },
    {
      label: "R6 - Aval no cumple",
      pm_label: "R6 - representante legal sin facultades",
      value: "r6",
      sub_options: [
        { label: "No vive dentro del domicilio", value: "r6-1", scope: "pf" },
        { label: "No cuenta con capacidad de pago", value: "r6-2", scope: "pf" },
        { label: "Alteración de documentos", value: "r6-3", scope: "pf" },
        { label: "Sin relación directa con el titular", value: "r6-4", scope: "pf" },
        { label: "No cumple con edad", value: "r6-5", scope: "pf" },
        { label: "Actividad fuera de política", value: "r6-6", scope: "pf" },
        { label: "Representante legal no cuenta con facultades de títulos de crédito", value: "r6-7", scope: "pm" },
      ],
    },
    {
      label: "R7 - Titular no desea el financiamiento",
      pm_label: "R7 - representante legal no desea financiamiento",
      value: "r7",
      sub_options: [
        { label: "Se decidió por otra opción de crédito", value: "r7-1", scope: "all" },
        { label: "Condiciones financieras", value: "r7-2", scope: "all" },
        { label: "Otros", value: "r7-3", scope: "all" },
        { label: "No acepta videollamada", value: "r7-4", scope: "pf" },
        { label: "Titular no desea continuar con la llamada", value: "r7-5", scope: "pf" },
        { label: "Titular no concluye proceso de videollamada", value: "r7-6", scope: "pf" },
        { label: "Representante legal no desea continuar con la llamada", value: "r7-7", scope: "pm" },
      ],
    },
  ];

  public static isRejectSubReasonApplicable(subOption: RejectSubOption, regime?: string): boolean {
    const scope: RejectRegimeScope = subOption.scope ?? "all";
    if (scope === "all" || !regime) {
      return true;
    }
    if (scope === "pm") {
      return regime === "pm";
    }
    return regime !== "pm";
  }

  public static getRejectOptionLabel(option: RejectOption, regime?: string): string {
    if (regime === "pm" && option.pm_label) {
      return option.pm_label;
    }
    return option.label;
  }

  public static getRejectOptions(regime?: string): RejectOption[] {
    return GeneralUtilities.reject_options.map((option) => ({
      ...option,
      label: GeneralUtilities.getRejectOptionLabel(option, regime),
    }));
  }

  public static getRejectedReasonLabel(rejectReason: string, regime?: string): string {
    const option = GeneralUtilities.reject_options.find((rejectOption) => rejectOption.value === rejectReason);
    if (option) {
      return GeneralUtilities.getRejectOptionLabel(option, regime);
    }
    return GeneralUtilities.rejected_reasons[rejectReason] ?? "";
  }

  public static isRejectOptionsCatalog(options: Array<{ value?: string }>): boolean {
    if (!options?.length || options.length !== GeneralUtilities.reject_options.length) {
      return false;
    }
    return options.every((option, index) => option.value === GeneralUtilities.reject_options[index].value);
  }

  public static getRejectSubOptions(rejectReason: string, regime?: string): RejectSubOption[] {
    const parent = GeneralUtilities.reject_options.find((option) => option.value === rejectReason);
    const subOptions = parent?.sub_options ?? [];
    if (!regime) {
      return subOptions;
    }
    return subOptions.filter((subOption) => GeneralUtilities.isRejectSubReasonApplicable(subOption, regime));
  }

  public static findRejectSubOption(
    rejectReason: string,
    subReason: string,
    regime?: string
  ): RejectSubOption | undefined {
    return GeneralUtilities.getRejectSubOptions(rejectReason, regime).find(
      (subOption) => subOption.value === subReason
    );
  }

  /** Document categories selectable when rejecting with R4 (documentos alterados) sub-reasons */
  public static readonly rejectSubReasonDocumentCategories: Record<string, readonly string[]> = {
    "r4-1": ["identification", "selfie_identification", "pm_identification"],
    "r4-2": ["address_proof", "pm_address_proof"],
    "r4-3": ["income_proof", "pm_income_proof"],
    "r4-4": ["complementary", "pm_income_complement"],
    "r4-5": ["pm_incorporation_articles"],
    "r4-6": ["tax_situation_proof", "pm_financial_situation"],
  };

  public static getRejectSubReasonDocumentCategories(subReason: string): readonly string[] {
    return GeneralUtilities.rejectSubReasonDocumentCategories[subReason] ?? [];
  }

  public static job_activities = {
    EmployeeJob: "Asalariado",
    RetiredJob: "Jubilado/Pensionado",
    IndependentJob: "Independiente",
    CompanyJob: "Empresa",
    MixedJob: "Mixta",
    GovernmentJob: "Asalariado",
  };

  public static job_seniority = {
    less_than_three_months: "0 - 3 meses",
    three_to_twelve_months: "3 - 12 meses",
    one_to_five_years: "1 - 5 años",
    more_than_five_years: "más de 5 años",
    less_than_one_year: "0 - 1 año",
    one_to_three_years: "1 año - 3 años",
    three_to_five_years: "3 años - 5 años",
  };

  public static phone_categories = [
    { value: "cellphone", label: "Celular" },
    { value: "home", label: "Casa" },
    { value: "work", label: "Trabajo" },
    { value: "legacy", label: "Sin definir" },
  ];

  public static address_categories = [
    { value: "current", label: "Actual" },
    { value: "current_rented", label: "Actual rentado" },
    { value: "not_current", label: "Domicilio anterior" },
    { value: "familiar", label: "Familiar " },
    { value: "fiscal", label: "Fiscal" },
    { value: "not_recognized", label: "No reconocido" },
    { value: "other", label: "Otra (especificar)" },
  ];

  public static job_description = {
    RetiredJob: "Jubilado",
    EmployeeJob: "Empleado",
    IndependentJob: "Trabajador independiente",
    GovernmentJob: "Trabajador gubernamental",
  };

  public static job_time_options = [
    { label: "0 - 3 meses", value: "less_than_three_months" },
    { label: "3 - 12 meses", value: "three_to_twelve_months" },
    { label: "1 - 5 años", value: "one_to_five_years" },
    { label: "más de 5 años", value: "more_than_five_years" },
  ];

  public static incident_options_address_proof = [
    { label: "Domicilio distinto al registrado", value: "Domicilio distinto al registrado" },
    { label: "Domicilio donde habita, no de trabajo", value: "Domicilio donde habita, no de trabajo" },
  ];

  public static incident_options_identification = [
    {
      label: "Identificación no está vigente en la lista nominal",
      value: "Identificación no está vigente en la lista nominal",
    },
    { label: 'Identificación en "trámite posterior"', value: 'Identificación en "trámite posterior"' },
  ];

  public static incident_options_income_proof = [
    { label: "Cuenta adicional (Sumar ingresos)", value: "Cuenta adicional (Sumar ingresos)" },
    { label: "Cuenta adicional (Portabilidad de nómina)", value: "Cuenta adicional (Portabilidad de nómina)" },
    { label: "Periodos adicionales (Sumar ingresos)", value: "Periodos adicionales (Sumar ingresos)" },
  ];
  // Kept as an alias of the backend-aligned regex; see email-validator.ts.
  public static readonly email_regex = VALID_EMAIL_REGEX;
  public static readonly phone_pattern = "[0-9 ]{10}";

  public static downloadBlob(blob, filename) {
    const fileExt = filename.split(".").pop();
    const newBlob = new Blob([blob], { type: GeneralUtilities.extensionToType[fileExt] });
    const nav = window.navigator as any;
    if (nav && nav.msSaveOrOpenBlob) {
      nav.msSaveOrOpenBlob(newBlob);
      return;
    }

    const data = window.URL.createObjectURL(newBlob);

    const link = document.createElement("a");
    link.href = data;
    link.download = filename;
    link.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));

    setTimeout(function () {
      window.URL.revokeObjectURL(data);
      link.remove();
    }, 100);
  }

  public static openBlobNewTab(blob) {
    const fileURL = window.URL.createObjectURL(blob);
    const tab = window.open();
    tab.location.href = fileURL;
  }

  public static downloadFile(imageBase64: string, fileName: string) {
    const linkSource = `data:image/png;base64,${imageBase64}`;
    const downloadLink = document.createElement("a");

    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  }

  public static stringToColor(str): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let colour = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      colour += `00${value.toString(16)}`.substr(-2);
    }
    return colour;
  }

  public static ljust(input: string, width: number, padding?: string) {
    padding = padding || " ";
    padding = padding.substr(0, 1);
    if (input.length < width) return input + padding.repeat(width - input.length);
    else return input;
  }

  public static rjust(input: string, width: number, padding?: string) {
    padding = padding || " ";
    padding = padding.substr(0, 1);
    if (input.length < width) return padding.repeat(width - input.length) + input;
    else return input;
  }

  public static escapeCsv(str: string): string {
    if (str != undefined) {
      return str.replace(",", "");
    } else {
      return "";
    }
  }

  public static jsonToCsv(arr): string {
    const array = [Object.keys(arr[0])].concat(arr);

    return array.map((it) => Object.values(it).toString()).join("\n");
  }

  public static beautyDate(date): string {
    const dt = new Date(date);
    return dt.toLocaleString("es-mx", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  public static copyContent(val: string, e?) {
    const selBox = document.createElement("textarea");
    selBox.style.position = "fixed";
    selBox.style.left = "0";
    selBox.style.top = "0";
    selBox.style.opacity = "0";
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand("copy");
    document.body.removeChild(selBox);
    if (!e) {
      return;
    }
    const tooltip = document.createElement("div");
    tooltip.style.cssText =
      "background: rgba(97, 97, 97, 0.9); font-size: 10px;color: #fff; border-radius: 4px; margin: 14px; max-width: 250px; padding: 6px 8px; overflow: hidden; text-overflow: ellipsis;";
    tooltip.style.position = "fixed";
    tooltip.style.left = `${e.clientX - 50}px`;
    tooltip.style.top = `${e.clientY}px`;
    tooltip.innerHTML = "Texto copiado";
    document.body.appendChild(tooltip);
    setTimeout(function () {
      document.body.removeChild(tooltip);
    }, 500);
  }

  public static titleize(sentence) {
    if (!sentence || !sentence.split) return sentence;

    const _titleizeWord = (string) => string.charAt(0).toUpperCase() + string.slice(1).toLowerCase(),
      result = [];
    sentence.split(" ").forEach(function (w) {
      if (w) {
        result.push(_titleizeWord(w));
      }
    });
    return result.join(" ").trim();
  }

  public static stateFromPostalCode(postalCode: string | number): string | null {
    const cp = Number(postalCode.toString().padStart(5, "0"));
    if (cp >= 1000 && cp <= 16999) return "CDMX";
    if (cp >= 20000 && cp <= 20999) return "AGUASCALIENTES";
    if (cp >= 21000 && cp <= 22999) return "BAJA_CALIFORNIA_NORTE";
    if (cp >= 23000 && cp <= 23999) return "BAJA_CALIFORNIA_SUR";
    if (cp >= 24000 && cp <= 24999) return "CAMPECHE";
    if (cp >= 25000 && cp <= 27999) return "COAHUILA";
    if (cp >= 28000 && cp <= 28999) return "COLIMA";
    if (cp >= 29000 && cp <= 30999) return "CHIAPAS";
    if (cp >= 31000 && cp <= 33999) return "CHIHUAHUA";
    if (cp >= 34000 && cp <= 35999) return "DURANGO";
    if (cp >= 36000 && cp <= 38999) return "GUANAJUATO";
    if (cp >= 39000 && cp <= 41999) return "GUERRERO";
    if (cp >= 42000 && cp <= 43999) return "HIDALGO";
    if (cp >= 44000 && cp <= 49999) return "JALISCO";
    if (cp >= 50000 && cp <= 57999) return "ESTADO_DE_MEXICO";
    if (cp >= 58000 && cp <= 61999) return "MICHOACAN";
    if (cp >= 62000 && cp <= 62999) return "MORELOS";
    if (cp >= 63000 && cp <= 63999) return "NAYARIT";
    if (cp >= 64000 && cp <= 67999) return "NUEVO_LEON";
    if (cp >= 68000 && cp <= 71999) return "OAXACA";
    if (cp >= 72000 && cp <= 75999) return "PUEBLA";
    if (cp >= 76000 && cp <= 76999) return "QUERETARO";
    if (cp >= 77000 && cp <= 77999) return "QUINTANA_ROO";
    if (cp >= 78000 && cp <= 79999) return "SAN_LUIS_POTOSI";
    if (cp >= 80000 && cp <= 82999) return "SINALOA";
    if (cp >= 83000 && cp <= 85999) return "SONORA";
    if (cp >= 86000 && cp <= 86999) return "TABASCO";
    if (cp >= 87000 && cp <= 89999) return "TAMAULIPAS";
    if (cp >= 90000 && cp <= 90999) return "TLAXCALA";
    if (cp >= 91000 && cp <= 96999) return "VERACRUZ";
    if (cp >= 97000 && cp <= 97999) return "YUCATAN";
    if (cp >= 98000 && cp <= 99999) return "ZACATECAS";

    return null;
  }

  public static cleanJSON(data) {
    const json = data;
    const clean_json = json.replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '"$2": ');
    const obj = JSON.parse(clean_json);
    return obj;
  }

  public static endorsement_option = {
    add_income: "Suma de ingresos",
    profiling: "Necesario por perfilamiento",
    add_income_and_profiling: "Suma de ingresos y necesario por perfilamiento",
  };

  public static endorsement_relation = {
    mate: "Cónyuge",
    child: "Hijo/a",
    guardian: "Padre/Madre",
    sibling: "Hermano/a",
  };

  public static formatDate(date: string): string {
    if (date == null) return "indefinido";
    const [year, month, day] = date.split("-");
    const monthNames = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

    const monthName = monthNames[parseInt(month) - 1];

    return `${parseInt(day)} ${monthName} ${year}`;
  }

  public static order_in_words = ["first", "second", "third", "fourth", "fifth", "sixth", "seventh", "eighth", "ninth"];

  public static year_options = Array.from({ length: 100 }, (_, i) => i);

  public static month_options = Array.from({ length: 13 }, (_, i) => i);

  public static car_use_options = [
    { label: "Dependiente económico", value: "dependiente economico" },
    { label: "Personal", value: "personal" },
    { label: "Plataformas digitales", value: "plataformas digitales" },
    { label: "Tercero", value: "tercero" },
    { label: "Trabajo", value: "trabajo" },
    { label: "Otro", value: "other" },
  ];

  public static banks = {
    bbva: "BBVA",
    cibanco: "CI Banco",
    stp: "STP",
  };

  public static payment_method = {
    deposit: "Depósito",
    spei: "SPEI",
    balance_in_favour: "Saldo a favor",
    payroll: "Nómina",
    dom: "Domiciliación",
    vat_condo: "Condonación de IVA",
    debt_condo: "Saldo por cobrar",
  };

  public static payment_reconcile = {
    deposit: "Depósito",
    spei: "SPEI",
  };

  public static payment_status = {
    unverified: "Pendiente de verificar",
    approved: "Aprobado",
    rejected: "Rechazado",
    reconcile: "Por conciliar",
    rejected_reconcile: "Conciliación rechazada",
  };

  public static history_payment_status = {
    unverified: "Conciliado",
    approved: "Aprobado",
    rejected: "Rechazado",
    rejected_reconcile: "Conciliación rechazada",
  };

  public static reconcile_banks = {
    bbva: "BBVA",
    cibanco: "CI Banco",
  };

  public static cancel_reasons = {
    0: "payment_not_found",
    1: "edited_receipt",
    2: "previous_receipt",
    3: "incorrect_account",
    4: "previous_reconcile",
  };

  public static cancel_options = [
    { label: "No se encuentra en nuestras cuentas", value: "0" },
    { label: "Comprobante de pago editado", value: "1" },
    { label: "Comprobante de un pago anterior", value: "2" },
    { label: "Cuenta benefeciaria incorrecta", value: "3" },
    { label: "Pago conciliado anteriormente", value: "4" },
  ];

  public static event_logs = [
    { value: "link-sent", viewValue: "Link PAD enviado" },
    { value: "link-reminder-sent", viewValue: "Link PAD reenviado" },
    { value: "link-opened", viewValue: "Link PAD abierto" },
    { value: "kyc-started", viewValue: "KYC: Iniciado" },
    { value: "kyc-reminder-sent", viewValue: "Identidad: Recordatorio enviado" },
    { value: "kyc-documents-sent", viewValue: "Identidad: Documentos enviados" },
    { value: "kyc-connection-error", viewValue: "Identidad: Error en el envío" },
    { value: "kyc-restarted", viewValue: "Identidad: Proceso reinciado" },
    { value: "kyc-success", viewValue: "Identidad: Documentos verificados" },
    { value: "kyc-failed", viewValue: "Identidad: Error en verificaciones" },
    { value: "kyc-not-valid", viewValue: "Identidad: Error en analisis" },
    { value: "address-started", viewValue: "Domicilio: Iniciado" },
    { value: "address-reminder-sent", viewValue: "Domicilio: Recordatorio enviado" },
    { value: "address-completed", viewValue: "Domicilio: Completado" },
    { value: "ob-started", viewValue: "Ingresos: Iniciado" },
    { value: "ob-reminder-sent", viewValue: "Ingresos: Recordatorio enviado" },
    { value: "ob-wrong-credentials", viewValue: "Ingresos: Credenciales incorrectas" },
    { value: "ob-instructions-sent", viewValue: "Ingresos: Instrucciones para credenciales enviadas" },
    { value: "ob-manual-upload-started", viewValue: "Ingresos: Carga manual de documentos" },
    { value: "ob-manual-upload-completed", viewValue: "Ingresos: Carga manual exitosa" },
    { value: "ob-manual-upload-approved", viewValue: "Ingresos: Documentos aprobados" },
    { value: "ob-manual-upload-postponed", viewValue: "Ingresos: Se pospone carga de documentos" },
    { value: "ob-completed", viewValue: "Ingresos: Completado" },
    { value: "references-completed", viewValue: "Referencias: Completado" },
    { value: "references-postponed", viewValue: "Referencias: Pospuestas" },
  ];

  public static app_stages = {
    draft: "Sin enviar",
    validation_process: "En proceso de validación (PAD)",
    onboarding: "Validación de identidad digital",
    approval_process: "En proceso de aprobación",
    approved: "Aprobado",
    purchase_order: "Orden de compra",
    contract: "Contrato",
    disbursement: "Desembolso",
    delivering: "En proceso de entrega",
    delivered: "Entregado",
    rejected: "Rechazado",
    lost: "Inactiva",
    expired: "Vencida",
  };

  public static stages_not_sent = ["draft", "obsolete"];

  public static calculateAge(date) {
    const parsed_date = new Date(date);
    parsed_date.setTime(parsed_date.getTime() + parsed_date.getTimezoneOffset() * 60 * 1000);
    const difference = Date.now() - parsed_date.getTime();
    const ageDate = new Date(difference);
    const age = Math.abs(ageDate.getUTCFullYear() - 1970);
    return age;
  }

  public static parseInsurances = {
    "1": "MAPFRE",
    "4": "GNP",
    "6": "ATLAS",
    "8": "QUALITAS",
    "15": "ANA",
    "22": "CHUBB",
    "31": "HDI",
  };

  public static tax_regimes = [
    {
      value: "moral_people_general",
      viewValue: "General de Ley Personas Morales",
    },
    {
      value: "moral_people_for_non_profit_purposes",
      viewValue: "Personas Morales con Fines no Lucrativos",
    },
    {
      value: "salaries_and_salaries_and_income_assimilated_to_wages",
      viewValue: "Sueldos y Salarios e Ingresos Asimilados a Salarios",
    },
    {
      value: "lease",
      viewValue: "Arrendamiento",
    },
    {
      value: "disposal_regime_or_acquisition_of_goods",
      viewValue: "Régimen de Enajenación o Adquisición de Bienes",
    },
    {
      value: "other_income",
      viewValue: "Demás ingresos",
    },
    {
      value: "residents_abroad_without_permanent_establishment_in_mexico",
      viewValue: "Residentes en el Extranjero sin Establecimiento Permanente en México",
    },
    {
      value: "dividend_income_partners_and_shareholders",
      viewValue: "Ingresos por Dividendos (socios y accionistas)",
    },
    {
      value: "natural_persons_with_business_and_professional_activities",
      viewValue: "Personas Físicas con Actividades Empresariales y Profesionales",
    },
    {
      value: "interest_income",
      viewValue: "Ingresos por intereses",
    },
    {
      value: "revenue_regime_for_obtaining_prizes",
      viewValue: "Régimen de los ingresos por obtención de premios",
    },
    {
      value: "no_tax_obligations",
      viewValue: "Sin obligaciones fiscales",
    },
    {
      value: "cooperative_production_societies_that_choose_to_defer_their_income",
      viewValue: "Sociedades Cooperativas de Producción que optan por diferir sus ingresos",
    },
    {
      value: "fiscal_incorporation",
      viewValue: "Incorporación Fiscal",
    },
    {
      value: "agricultural_livestock_silvicultural_and_fishing_activities",
      viewValue: "Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras",
    },
    {
      value: "optional_for_societies",
      viewValue: "Opcional para Grupos de Sociedades",
    },
    {
      value: "coordinated",
      viewValue: "Coordinados",
    },
    {
      value: "regime_of_business_activities_with_income_through_technological_platforms",
      viewValue: "Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas",
    },
    {
      value: "simplified_trust_regime",
      viewValue: "Régimen Simplificado de Confianza",
    },
  ];

  public static address_types = {
    family: "Familiar",
    friend: "Amistad",
    work: "Trabajo",
    main: "Principal",
    house: "Casa",
    bureau: "Buró de crédito",
    validation_process: "Proceso de validación",
    declared: "Declarado",
    current_rented: "Rentado actualmente",
    not_current: "Antiguo",
    fiscal: "Fiscal",
    not_recognized: "No reconocido",
    other: "Otro",
    income_proof: "Comprobante de ingresos",
    identification: "Indentificación",
    address_proof: "Comprobante de domicilio",
  };

  public static address_options = [
    { value: "family", label: "Familiar" },
    { value: "friend", label: "Amistad" },
    { value: "work", label: "Trabajo" },
    { value: "main", label: "Principal" },
    { value: "house", label: "Casa" },
    { value: "bureau", label: "Buró de crédito" },
    { value: "validation_process", label: "Proceso de validación" },
    { value: "declared", label: "Declarado" },
    { value: "current_rented", label: "Rentado actualmente" },
    { value: "not_current", label: "Antiguo" },
    { value: "fiscal", label: "Fiscal" },
    { value: "not_recognized", label: "No reconocido" },
    { value: "other", label: "Otro" },
    { value: "income_proof", label: "Comprobante de ingresos" },
    { value: "identification", label: "Indentificación" },
    { value: "address_proof", label: "Comprobante de domicilio" },
  ];

  public static pdvStateRegions = {
    Chiapas: "Sur",
    Hidalgo: "Centro",
    "San Luis Potosí": "Centro-Norte",
    "Baja California": "Norte",
    "Veracruz de Ignacio de la Llave": "Sur",
    Aguascalientes: "Centro-Norte",
    Yucatán: "Sur",
    "Estado de México": "Centro",
    Colima: "Centro-Norte",
    Zacatecas: "Norte",
    Tamaulipas: "Norte",
    "Michoacán de Ocampo": "Centro",
    Chihuahua: "Norte",
    "Ciudad de México": "Centro",
    Tlaxcala: "Centro",
    Durango: "Norte-Occidente",
    "Baja California Sur": "Norte",
    Sonora: "Norte",
    Jalisco: "Centro",
    Nayarit: "Centro-Norte",
    Guerrero: "Sur",
    Oaxaca: "Sur",
    Morelos: "Centro",
    "Coahuila de Zaragoza": "Norte",
    Puebla: "Centro",
    "Nuevo León": "Norte",
    Querétaro: "Centro",
    Campeche: "Sur",
    Tabasco: "Sur",
    Sinaloa: "Norte",
    "Quintana Roo": "Sur",
    Guanajuato: "Centro",
  };

  public static mexico_states = [
    { id: null, view: "Sin información", code: null },
    { id: "CDMX", view: "Ciudad de México", code: 33 },
    { id: "MOR", view: "Morelos", code: 17 },
    { id: "EM", view: "Estado de México", code: 11 },
    { id: "JAL", view: "Jalisco", code: 15 },
    { id: "HGO", view: "Hidalgo", code: 14 },
    { id: "BCN", view: "Baja California", code: 2 },
    { id: "BCS", view: "Baja California Sur", code: 3 },
    { id: "DGO", view: "Durango", code: 10 },
    { id: "MICH", view: "Michoacan de Ocampo", code: 16 },
    { id: "OAX", view: "Oaxaca", code: 20 },
    { id: "QR", view: "Quintana Roo", code: 23 },
    { id: "SIN", view: "Sinaloa", code: 25 },
    { id: "TLA", view: "Tlaxcala", code: 29 },
    { id: "ZAC", view: "Zacatecas", code: 32 },
    { id: "QRO", view: "Queretaro", code: 22 },
    { id: "PUE", view: "Puebla", code: 21 },
    { id: "NAY", view: "Nayarit", code: 18 },
    { id: "COL", view: "Colima", code: 8 },
    { id: "GTO", view: "Guanajuato", code: 12 },
    { id: "COA", view: "Coahuila", code: 7 },
    { id: "NL", view: "Nuevo Leon", code: 19 },
    { id: "TAM", view: "Tamaulipas", code: 28 },
    { id: "CHI", view: "Chihuahua", code: 6 },
    { id: "AGS", view: "Aguascalientes", code: 1 },
    { id: "SLP", view: "San Luis Potosi", code: 24 },
    { id: "VER", view: "Veracruz", code: 30 },
    { id: "GRO", view: "Guerrero", code: 13 },
    { id: "YUC", view: "Yucatan", code: 31 },
    { id: "SON", view: "Sonora", code: 26 },
    { id: "CAM", view: "Campeche", code: 4 },
    { id: "TAB", view: "Tabasco", code: 27 },
    { id: "CHS", view: "Chiapas", code: 5 },
    // BureauAddress not standard states abbreviations
    { id: "ME", view: "Estado de México", code: 11 },
    { id: "BC", view: "Baja California", code: 2 },
    { id: "MIC", view: "Michoacan de Ocampo", code: 16 },
    { id: "QROO", view: "Quintana Roo", code: 23 },
    { id: "TLAX", view: "Tlaxcala", code: 29 },
    { id: "COAH", view: "Coahuila", code: 7 },
    { id: "CHIH", view: "Chihuahua", code: 6 },
    { id: "CHIS", view: "Chiapas", code: 5 },
  ];

  public static replaceAccents(text: string) {
    const letters = {
      á: "a",
      é: "e",
      í: "i",
      ó: "o",
      ú: "u",
      à: "a",
      è: "e",
      ì: "i",
      ò: "o",
      ù: "u",
      ñ: "n",
      Á: "A",
      É: "E",
      Í: "I",
      Ó: "O",
      Ú: "U",
      À: "A",
      È: "E",
      Ì: "I",
      Ò: "O",
      Ù: "U",
      Ñ: "N",
    };
    const expr = /[áàéèíìóòúùñ]/gi;
    return text.replace(expr, function (letter) {
      return letters[letter];
    });
  }

  public static filterWords<T extends Record<string, any>, K extends keyof T>(
    wordSearch: string,
    datas: T[],
    keySearch: K
  ) {
    const pieces = this.replaceAccents(wordSearch.toLowerCase()).split(" ");
    const filtered = datas.filter((data) => {
      let count_found = 0;
      for (const i in pieces) {
        if (this.replaceAccents(data[keySearch].toLowerCase()).includes(pieces[i])) {
          count_found++;
        }
        if (pieces.length == count_found) break;
      }
      return count_found == pieces.length;
    });
    return filtered;
  }

  public static income_categories = [
    {
      name: "Agencia de marketing y publicidad",
      value: "marketing",
    },
    {
      name: "Agencia de viajes",
      value: "travel_agency",
    },
    {
      name: "Arrendamiento",
      value: "leasing",
    },
    {
      name: "Bróker",
      value: "broker",
    },
    {
      name: "Comercio de productos médicos",
      value: "medical_products",
    },
    {
      name: "Comercio informal",
      value: "informal_commerce",
    },
    {
      name: "Construcción",
      value: "construction",
    },
    {
      name: "Contratistas",
      value: "contractor",
    },
    {
      name: "E-commerce",
      value: "ecommerce",
    },
    {
      name: "Ganadería y agricultura",
      value: "farming",
    },
    {
      name: "Hotelería",
      value: "hotel",
    },
    {
      name: "Negocios minoristas",
      value: "retail_business",
    },
    {
      name: "Renta de maquinaria y equipo",
      value: "machinery",
    },
    {
      name: "Renta de vehículos utilitarios",
      value: "vehicle_rent",
    },
    {
      name: "Restaurantes (Alimentos)",
      value: "restaurant",
    },
    {
      name: "Servicios de mantenimiento",
      value: "maintenance",
    },
    {
      name: "Servicios profesionales",
      value: "professional_services",
    },
    {
      name: "Taller mecánico",
      value: "mechanical_workshop",
    },
    {
      name: "Transporte (Logística)",
      value: "transport",
    },
    {
      name: "Aval",
      value: "endorsement",
    },
  ];

  public static validateFormatNumber(event: any): void {
    let key = event.keyCode;
    key = String.fromCharCode(key);
    const regex = /[0-9]|\./;
    if (!regex.test(key)) {
      event.returnValue = false;
      if (event.preventDefault) {
        event.preventDefault();
      }
    }
  }

  public static cleanPhoneNumber(phone: string): string {
    phone = phone.replace(/\s/g, "");

    if (phone.length === 10) {
      return `+52${phone}`;
    } else if (phone[0] !== "+") {
      return `+${phone}`;
    } else {
      return phone;
    }
  }

  public static reject_reasons_reviewed = ["r4"];

  public static translateSeniority(seniority: string) {
    const switchObject = {
      less_than_three_months: () => `Menos de 3 meses`,
      three_to_six_months: () => `3 a 6 meses`,
      six_months_to_one_year: () => `6 meses a 1 año`,
      three_to_twelve_months: () => `3 a 12 meses`,
      one_to_two_years: () => `1 a 2 años`,
      two_to_three_years: () => `2 a 3 años`,
      more_than_five_years: () => `Más de 5 años`,
      one_to_five_years: () => `1 a 5 años`,
      default: () => seniority,
    };

    const caseFunction = switchObject[seniority] || switchObject["default"];
    return caseFunction();
  }

  public static translateJobType(job_type: string) {
    const switchObject = {
      EmployeeJob: () => `Empleado`,
      IndependentJob: () => `Independiente`,
      RetiredJob: () => `Jubilado`,
      GovernmentJob: () => `Empleado`,
      default: () => job_type,
    };

    const caseFunction = switchObject[job_type] || switchObject["default"];
    return caseFunction();
  }

  public static toRailsDate(date: Date) {
    const timezone = -date.getTimezoneOffset();
    const diference = timezone >= 0 ? "+" : "-";

    return `${date.getFullYear()}-${this.datePadding(date.getMonth() + 1)}-${this.datePadding(
      date.getDate()
    )} ${this.datePadding(date.getHours())}:${this.datePadding(date.getMinutes())}:${this.datePadding(
      date.getSeconds()
    )} ${diference}${this.datePadding(Math.floor(Math.abs(timezone) / 60))}${this.datePadding(
      Math.abs(timezone) % 60
    )}`;
  }

  private static datePadding(num) {
    return (num < 10 ? "0" : "") + num;
  }

  public static format_time_ellapsed(milliseconds: number) {
    let seconds = Math.floor(milliseconds / 1000);
    let minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    seconds = seconds % 60;
    minutes = minutes % 60;
    const formatted_seconds = seconds < 10 ? `0${seconds.toString()}` : seconds;
    const formatted_minutes = minutes < 10 ? `0${minutes.toString()}` : minutes;
    return `${hours}:${formatted_minutes}:${formatted_seconds}`;
  }

  public static external_relations = {
    salesman: "Asesor comercial",
    coordinator: "Coordinador",
    fi: "F&I",
    top: "TOP",
    fi_asistant: "Asistente F&I",
    financial_representative: "Representante financiera",
    online_salesman: "Asesor comercial online",
    commercial_manager: "Gerente comercial",
  };

  public static external_relations_options = [
    { value: "salesman", label: "Asesor comercial", disabled: false },
    { value: "coordinator", label: "Coordinador", disabled: false },
    { value: "fi", label: "F&I", disabled: false },
    { value: "fi_asistant", label: "Asistente F&I", disabled: false },
    { value: "financial_representative", label: "Representante financiera", disabled: false },
    { value: "online_salesman", label: "Asesor comercial online", disabled: false },
    { value: "commercial_manager", label: "Gerente comercial", disabled: false },
  ];
  public static contribution_types = {
    automotive_lease: "Renta",
    automotive_lease_term: "Plazo",
    automotive_credit: "Renta",
    automotive_credit_term: "Plazo",
  };

  public static load_subtype = [
    { value: { value: "NA", load_type: "_carga_a" }, viewValue: "NO LLEVA CARGA" },
    { value: { value: "abarrotes", load_type: "_carga_a" }, viewValue: "ABARROTES" },
    { value: { value: "alimentos", load_type: "_carga_a" }, viewValue: "ALIMENTOS VARIOS" },
    { value: { value: "aluminio", load_type: "_carga_b" }, viewValue: "ALUMINIO Y VIDRIO" },
    { value: { value: "anuncios", load_type: "_carga_b" }, viewValue: "ANUNCIOS Y LO RELACIONADO" },
    { value: { value: "deportivos", load_type: "_carga_a" }, viewValue: "ARTICULOS DEPORTIVOS" },
    { value: { value: "hogar_esp", load_type: "_carga_a" }, viewValue: "ARTICULOS PARA EL HOGAR (ESPORÁDICO)" },
    {
      value: { value: "hogar_comercial", load_type: "_carga_b" },
      viewValue: "ARTICULOS PARA EL HOGAR (USO COMERCIAL)",
    },
    { value: { value: "avicultura", load_type: "_carga_a" }, viewValue: "AVICULTURA Y LO RELACIONADO" },
    { value: { value: "banquetes", load_type: "_carga_a" }, viewValue: "BANQUETES" },
    { value: { value: "bebidas_alc", load_type: "_carga_a" }, viewValue: "BEBIDAS ALCOHOLICAS" },
    { value: { value: "bebidas_emb", load_type: "_carga_a" }, viewValue: "BEBIDAS EMBOTELLADAS" },
    { value: { value: "carnes", load_type: "_carga_a" }, viewValue: "CARNES Y EMBUTIDOS" },
    { value: { value: "convertidor", load_type: "_carga_a" }, viewValue: "CONVERTIDOR" },
    { value: { value: "desperdicios", load_type: "_carga_b" }, viewValue: "DESPERDICIOS INDUSTRIALES NO PELIGROSOS" },
    { value: { value: "eqp_med", load_type: "_carga_a" }, viewValue: "EQUIPO MEDICO Y LO RELACIONADO" },
    { value: { value: "ferreteria ", load_type: "_carga_b" }, viewValue: "FERRETERIA, PLOMERIA, TLAPALERIA" },
    { value: { value: "flores", load_type: "_carga_a" }, viewValue: "FLORES" },
    { value: { value: "flores_adc", load_type: "_carga_a" }, viewValue: "FLORES FRUTAS LEGUMBRES Y ABARROTES" },
    { value: { value: "ganado", load_type: "_carga_b" }, viewValue: "GANADO EN GENERAL" },
    { value: { value: "herreria", load_type: "_carga_b" }, viewValue: "HERRERIA" },
    { value: { value: "hielo", load_type: "_carga_a" }, viewValue: "HIELO" },
    { value: { value: "remolque", load_type: "_carga_b" }, viewValue: "JALA REMOLQUE" },
    { value: { value: "jardineria", load_type: "_carga_a" }, viewValue: "JARDINERIA Y SIMILAR" },
    { value: { value: "jueguetes", load_type: "_carga_a" }, viewValue: "JUGUETES" },
    { value: { value: "lacteos", load_type: "_carga_a" }, viewValue: "LACTEOS Y LO RELACIONADO" },
    { value: { value: "lavanderia", load_type: "_carga_a" }, viewValue: "LAVANDERIA Y TINTORERIA" },
    { value: { value: "papeleria", load_type: "_carga_a" }, viewValue: "LITOGRAFIA Y PAPELERIA" },
    { value: { value: "maquinaria_gral", load_type: "_carga_b" }, viewValue: "MAQUINARIA Y EQUIPO EN GENERAL" },
    { value: { value: "material_elec", load_type: "_carga_b" }, viewValue: "MATERIAL ELECTRICO" },
    { value: { value: "material_const", load_type: "_carga_b" }, viewValue: "MATERIALES DE CONSTRUCCION" },
    { value: { value: "metales", load_type: "_carga_b" }, viewValue: "METALES" },
    { value: { value: "muebles_esp", load_type: "_carga_a" }, viewValue: "MUEBLES EN GENERAL (ESPORÁDICO)" },
    { value: { value: "muebles_comercial", load_type: "_carga_b" }, viewValue: "MUEBLES EN GENERAL (USO COMERCIAL)" },
    { value: { value: "oxidantes", load_type: "_carga_a" }, viewValue: "OXIDANTES Y PEROXIDOS ORGANICOS" },
    { value: { value: "pan", load_type: "_carga_a" }, viewValue: "PAN Y SIMILARES" },
    { value: { value: "papel_ind", load_type: "_carga_a" }, viewValue: "PAPEL INDUSTRIAL" },
    { value: { value: "papeleria_gral", load_type: "_carga_a" }, viewValue: "PAPELERIA EN GENERAL" },
    {
      value: { value: "autopartes_ref", load_type: "_carga_b" },
      viewValue: "PARTES AUTOMOTRICES Y REFACCIONES EN GRAL.",
    },
    { value: { value: "personal", load_type: "_carga_a" }, viewValue: "PERSONAL" },
    { value: { value: "pipas", load_type: "_carga_c" }, viewValue: "PIPA" },
    { value: { value: "plasticos", load_type: "_carga_a" }, viewValue: "PLÁSTICOS" },
    {
      value: { value: "agricolas_campo", load_type: "_carga_a" },
      viewValue: "PRODUCTOS AGRICOLAS Y LO RELACIONADO AL CAMPO",
    },
    { value: { value: "agricolas_ganado", load_type: "_carga_a" }, viewValue: "PRODUCTOS AGRICOLAS Y GANADO" },
    { value: { value: "farmaceuticos", load_type: "_carga_a" }, viewValue: "PRODUCTOS FARMACEUTICOS" },
    { value: { value: "forestales", load_type: "_carga_a" }, viewValue: "PRODUCTOS FORESTALES" },
    { value: { value: "minerales_no_meta", load_type: "_carga_a" }, viewValue: "PRODUCTOS MINERALES NO METALURGICOS" },
    { value: { value: "quimicos_no_pel", load_type: "_carga_c" }, viewValue: "PRODUCTOS QUIMICOS NO PELIGROSOS" },
    { value: { value: "mineria", load_type: "_carga_a" }, viewValue: "RELACIONADO CON LA MINERIA" },
    { value: { value: "ropa_calzado", load_type: "_carga_a" }, viewValue: "ROPA Y CALZADO" },
    { value: { value: "textiles", load_type: "_carga_a" }, viewValue: "TEXTILES" },
    { value: { value: "trans_moto", load_type: "_carga_a" }, viewValue: "TRANSPORTE DE MOTOCICLETAS" },
    { value: { value: "casa_hab", load_type: "_carga_a" }, viewValue: "USO DE CASA HABITACION" },
    { value: { value: "veterinaria", load_type: "_carga_a" }, viewValue: "VETERINARIA Y LO RELACIONADO" },
    { value: { value: "vidrieria", load_type: "_carga_b" }, viewValue: "VIDRIERIA" },
  ];

  public static mops_descriptions = {
    0: "Cuenta nueva",
    1: "Con pagos puntuales",
    2: "Atraso de 1 a 29 días",
    3: "Atraso de 30 a 59 días",
    4: "Atraso de 60 a 89 días",
    5: "Atraso de 90 a 119 días",
    6: "Atraso de 120 a 149 días",
    7: "Atraso de 150 o más días",
    96: "Deuda parcial o sin recuperar",
    97: "Deuda en demanda o juicio mercantil",
  };

  public static onboarding_steps = {
    digital: "Cliente no ha iniciado su validación",
    privacy: "Leyendo aviso de privacidad",
    otp: "Verificando teléfono (código enviado)",
    otp_failed: "Error en verificación del teléfono",
    identity: "Subiendo identificación",
    expired_identity: "Identificación inválida (requiere actualización)",
    reviewing: "Información en revisión por analista",
    address: "Subiendo comprobante de domicilio",
    validating_address: "Validando comprobante de domicilio",
    incomes: "Subiendo comprobantes de ingresos",
    missing: "Sin información disponible",
    link: "Link enviado a cliente (pendiente de abrir)",
    documents: "Documentos incorrectos o no vigentes",
    documents_review: "Revisando documentos cargados",
    document_upload: "Pendiente de subir documentos requeridos",
    credit_history: "Falta historial crediticio",
    waiting_call_time: "Agendando cita o llamada",
    waiting_link: "Enviando link al cliente",
    references: "Agregando referencias",
    record_update: "Esperando actualización del expediente",
    completed: "Validación completada",
    aborted: "Proceso cancelado manualmente",
    canceled: "Onboarding cancelado",
    expired: "Proceso expirado (no se completó a tiempo)",
  };

  public static reference_categories = {
    friendship: "Amigo",
    child: "Hijo/a",
    partner: "Cónyuge",
    father: "Padre",
    mother: "Madre",
    marriage: "Esposo/a",
    cousin: "Primo/a",
    sibling: "Hermano/a",
    "SI coincide": "Sí coincide",
    "NO coincide": "No coincide",
  };

  public static workflow_statuses = {
    additional_call_started: "En llamada adicional",
    additional_calls_started: "Esperando llamadas adicionales",
    additional_income_doc_review: "En validación de documentos de ingresos adicionales",
    approved: "Aprobado",
    audit_rejection: "En validación de rechazo",
    audit_risk_approval: "En revisión de aprobación con alto riesgo de transacción",
    calls_started: "En llamadas de validación",
    changes_approval: "Cambios post aprobación",
    changes_approval_analysis: "En análisis de cambios post aprobación",
    contract_visit: "Firma de contrato",
    fallen: "Solicitud cancelada",
    final_decision_idle: "En análisis para decisión final",
    in_additional_documents_after_call_validation: "En validación de documentos adicionales después de llamada",
    in_additional_documents_before_decision_validation: "Esperando resultado de validación de documentos",
    in_additional_documents_validation: "En validación de documentos adicionales",
    in_document_validation: "En validación de documentos",
    rate_additional_income_docs: "En validación de documentos de ingresos adicionales",
    rate_risk_approval_documents: "En validación de transacciones riesgosas",
    rejected: "Rechazado",
    review_expired_analysis: "En validación de aprobación vencida",
    review_requested_documents: "En validación de documentos requeridos",
    un_rejected: "Rechazo cancelado",
    validation_call_started: "En llamada de validación",
    waiting_additional_call: "Esperando llamada adicional",
    waiting_additional_call_resolution: "Esperando resultado de llamada adicional",
    waiting_additional_calls: "Esperando llamadas adicionales",
    waiting_additional_documents: "Esperando documentos adicionales",
    waiting_additional_documents_after_call: "Esperando documentos adicionales después de llamada",
    waiting_additional_documents_after_call_analysis: "Analizando documentos adicionales después de llamada",
    waiting_additional_documents_analysis: "Analizando documentos adicionales",
    waiting_additional_documents_before_decision: "Esperando documentos adicionales",
    waiting_additional_documents_before_decision_analysis: "Analizando documentos adicionales",
    waiting_additional_income_docs_review: "Esperando validación de documentos de ingresos adicionales",
    waiting_analysis: "Esperando asignación de analista",
    waiting_audit_rejection: "Esperando validación de rechazo",
    waiting_audit_risk_approval: "Esperando revisión de coordinador de riesgo",
    waiting_call_resolution: "Esperando resultado de la llamada de validación",
    waiting_contacts_for_calls: "Esperando contactos para llamadas adicionales",
    waiting_final_decision: "Esperando decisión final",
    waiting_for_additional_references: "Esperando referencias adicionales",
    waiting_meeting: "Esperando videollamada",
    waiting_reference_call: "Esperando llamada a referencias",
    waiting_reference_call_resolution: "Esperando resultado de la llamada a referencias",
    waiting_requested_documents: "Esperando documentos requeridos",
    waiting_reschedule_additional_call: "Esperando nuevo horario para llamada adicional",
    waiting_reschedule_call: "Esperando nuevo horario para llamada",
    waiting_review_changes_approval_analysis: "Esperando análisis de cambios post aprobación",
    waiting_review_expired: "Esperando revisión de aprobación vencida",
    waiting_review_requested_documents: "Esperando validación de documentos requeridos",
    waiting_updated_info: "Esperando documentos adicionales/actualizados",
    waiting_validation_call: "Esperando llamada de validación",
    waiting_visit_resolution: "Esperando resultado de visita de validación",
  };
  public static workflow_statuses_description = {
    in_document_validation:
      "Revisa que todos los documentos estén completos, vigentes, legibles y pasen los candados de seguridad",
    waiting_validation_call: "Realiza al menos 2 intentos de contacto al titular",
    waiting_call_resolution: "Analiza la información de la llamada y compárala contra los documentos",
    waiting_reference_call: "Realiza al menos 2 intentos de contacto a cada una de las 3 referencias del titular",
    in_additional_documents_validation: "Revisa los documentos adicionales solicitados",
    in_additional_documents_after_call_validation: "Revisa los documentos adicionales solicitados después de llamada",
    waiting_reference_call_resolution:
      "Analiza la información de las referencias y compárala contra la información obtenida anteriormente",
    waiting_additional_call: "Realiza una nueva llamada de validación al titular para aclarar dudas/inconsistencias",
    waiting_additional_call_resolution:
      "Analiza la información de la nueva llamada y compárala contra la información obtenida anteriormente",
    in_additional_documents_before_decision_validation: "Valida los documentos adicionales solicitados",
    waiting_final_decision:
      "Analiza y determina, con toda la información obtenida, si el cliente es viable o no y con qué condiciones",
    waiting_visit_resolution: "Compara la información obtenida de la visita con la información obtenida anteriormente",
    final_decision_idle: "Asegurate de tener toda la información y documentos necesarios para completar el análisis",
    approved: "Actualiza el estatus en el que se encuentra el cliente",
    changes_approval: "Analiza las últimas modificaciones a la solicitud",
    changes_approval_analysis: "Analiza las últimas modificaciones a la solicitud",
    review_requested_documents: "Revisa los documentos solicitados como requeridos",
    waiting_calls: "Realiza llamadas al titular y a las referencias",
    waiting_additional_calls: "Realiza llamadas adicionales al titular y/o referencias",
    calls_started: "Realizar las llamadas al titular y a las referencias",
    additional_calls_started: "Realizar las llamadas adicionales al titular y a las referencias",
    waiting_review_requested_documents: "Esperando a revisar documentos solicitados como requeridos",
    waiting_review_changes_approval_analysis: "Analiza las últimas modificaciones a la solicitud",
    audit_rejection: "Revisa el rechazo de la solicitud",
    waiting_audit_rejection: "Esperando a revisar el rechazo de la solicitud",
    waiting_audit_risk_approval: "Esperando revisión de coordinador de riesgo",
    audit_risk_approval: "Revisa la aprobación con alto riesgo de transacción",
    rate_risk_approval_documents: "Revisa los documentos de transacciones riesgosas",
    waiting_meeting: "Realiza la videollamada con el cliente",
    waiting_additional_income_docs_review: "Esperando a revisar documentos de ingresos adicionales",
    rate_additional_income_docs: "Revisa los documentos de ingresos adicionales",
    additional_income_doc_review: "Revisa los documentos de ingresos adicionales",
    waiting_review_expired: "Esperando a revisar aprobación vencida",
    review_expired_analysis: "En revisión de aprobación vencida",
  };
}
